import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { SparkleLoader } from '../../../../components/common/SparkleLoader'
import { AVATAR_COLOR, getInitials } from './avatarUtils'
import { BORDER_RADIUS, FONT_WEIGHT, TYPOGRAPHY } from '../../../../styles/theme'

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const SIZE_MAP: Record<AvatarSize, { container: number; loader: number; fontSize: string }> = {
  xs: { container: 24, loader: 16, fontSize: TYPOGRAPHY.caption.fontSize },
  sm: { container: 32, loader: 20, fontSize: TYPOGRAPHY.caption.fontSize },
  md: { container: 48, loader: 32, fontSize: TYPOGRAPHY.caption.fontSize },
  lg: { container: 80, loader: 64, fontSize: TYPOGRAPHY.h4.fontSize },
  xl: { container: 120, loader: 96, fontSize: TYPOGRAPHY.h3.fontSize },
}

interface StakeholderAvatarProps {
  /** Name of the stakeholder (used for initials fallback) */
  name: string
  /** URL of the portrait image */
  portraitUrl?: string | null
  /** Whether the portrait is currently being generated */
  isGenerating?: boolean
  /** Size preset */
  size?: AvatarSize
  /** Custom size in pixels (overrides size preset) */
  customSize?: number
  /** Whether to use square or rounded corners */
  variant?: 'rounded' | 'square'
  /** Optional className for additional styling */
  className?: string
}

const AvatarContainer = styled.div<{
  $size: number
  $variant: 'rounded' | 'square'
}>`
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border-radius: ${(props) => (props.$variant === 'square' ? BORDER_RADIUS.md : '50%')};
  background: ${AVATAR_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const AvatarInitials = styled.span<{ $fontSize: string }>`
  color: white;
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: ${(props) => props.$fontSize};
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`

/**
 * StakeholderAvatar - Reusable avatar component for stakeholders.
 * Displays portrait image, loading animation, or initials fallback.
 */
export function StakeholderAvatar({
  name,
  portraitUrl,
  isGenerating = false,
  size = 'md',
  customSize,
  variant = 'rounded',
  className,
}: StakeholderAvatarProps) {
  const sizeConfig = SIZE_MAP[size]
  const containerSize = customSize ?? sizeConfig.container
  const loaderSize = customSize ? Math.floor(customSize * 0.67) : sizeConfig.loader
  const fontSize = customSize ? `${Math.floor(containerSize * 0.35)}px` : sizeConfig.fontSize

  const [imgError, setImgError] = useState(false)
  useEffect(() => {
    setImgError(false)
  }, [portraitUrl])

  return (
    <AvatarContainer $size={containerSize} $variant={variant} className={className}>
      {portraitUrl && !imgError ? (
        <AvatarImage src={portraitUrl} alt={name} onError={() => setImgError(true)} />
      ) : isGenerating ? (
        <SparkleLoader isAnimating size={loaderSize} />
      ) : (
        <AvatarInitials $fontSize={fontSize}>{getInitials(name)}</AvatarInitials>
      )}
    </AvatarContainer>
  )
}
